import { FileText, Quote } from "lucide-react";
import { FlowDiagram, TechTag, Disclaimer } from "./common";
import SOP_EXCERPT from "../content/sopExcerpt";
import deepCase from "../content/qms_deep_case_study.json";

const ANNOTATIONS = [
  "Action-oriented instructions",
  "Clear SHALL / DO NOT expectations",
  "Objective verification",
  "Defined escalation",
  "Traceability",
  "GDP / ALCOA+",
  "Investigation → change → document → training → effectiveness",
];

const LIFECYCLE_LAYER = [
  {
    title: "Investigation → Controlled Change → Effective Document",
    items: deepCase.lifecycle.map((s) => ({ label: s.stage, detail: s.description })),
  },
];

// Minimal markdown-ish renderer tuned for the known SOP excerpt structure.
function renderExcerpt(md) {
  return md.split("\n").map((line, i) => {
    const t = line.trim();
    if (!t) return <div key={i} className="h-2" />;
    if (t.startsWith("# "))
      return (
        <p key={i} className="font-mono text-xs uppercase tracking-[0.2em] text-cyan mt-4">
          {t.slice(2)}
        </p>
      );
    if (t.startsWith("## "))
      return (
        <p key={i} className="font-sans text-sm font-semibold text-paper mt-4 border-b border-cyan/15 pb-1">
          {t.slice(3)}
        </p>
      );
    if (t.startsWith("### "))
      return (
        <p key={i} className="font-sans text-xs font-semibold text-pale mt-3">
          {t.slice(4)}
        </p>
      );
    if (t.startsWith("**") && t.includes(":**")) {
      const [label, ...rest] = t.replace(/\*\*/g, "").split(/:(.+)/);
      return (
        <p key={i} className="text-xs text-pale/85 mt-1.5">
          <span className="font-mono text-cyan/90">{label}:</span>
          {rest.join(":")}
        </p>
      );
    }
    if (t.startsWith("> "))
      return (
        <p key={i} className="text-xs text-warn/90 border-l-2 border-warn/50 pl-3 mt-2 italic">
          {t.slice(2).replace(/\*\*/g, "")}
        </p>
      );
    if (/^\d+\./.test(t))
      return (
        <p key={i} className="text-xs text-pale/85 mt-1 pl-4">
          {t}
        </p>
      );
    if (t.startsWith("- "))
      return (
        <p key={i} className="text-xs text-pale/80 mt-0.5 pl-4">
          • {t.slice(2)}
        </p>
      );
    return (
      <p key={i} className="text-xs text-pale/85 mt-1.5 leading-relaxed">
        {t.replace(/\*\*/g, "")}
      </p>
    );
  });
}

export default function QmsDeepCase() {
  return (
    <div className="mt-10" data-testid="qms-deep-case">
      <div className="panel p-5 border-l-2 border-l-cyan">
        <p className="overline-label">Synthetic Scenario</p>
        <p className="font-sans text-base text-paper mt-2">
          Platform: {deepCase.scenario.product} <span className="font-mono text-[10px] text-steel">(fictional)</span>
        </p>
        <p className="text-sm text-pale/85 mt-2 leading-relaxed">{deepCase.scenario.issue}</p>
        <p className="font-mono text-[10px] text-steel mt-2">{deepCase.scenario.context}</p>
      </div>

      <div className="mt-8">
        <h3 className="font-sans tracking-tight text-xl text-paper mb-4">From Investigation to Effective Document</h3>
        <FlowDiagram layers={LIFECYCLE_LAYER} testId="qms-deep-lifecycle" />
      </div>

      <div className="mt-10">
        <h3 className="font-sans tracking-tight text-xl text-paper">Writing Principles Applied</h3>
        <div className="grid sm:grid-cols-2 gap-2 mt-4" data-testid="qms-deep-principles">
          {deepCase.writingPrinciples.map((p) => (
            <div key={p} className="panel p-3.5 flex items-start gap-2.5">
              <Quote className="w-3.5 h-3.5 text-cyan mt-0.5 shrink-0" aria-hidden="true" />
              <p className="text-xs text-pale/85 leading-relaxed">{p}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-10">
        <div className="flex items-center gap-3 flex-wrap">
          <h3 className="font-sans tracking-tight text-xl text-paper">Synthetic Redacted SOP Excerpt</h3>
          <span className="tech-tag">Writing-style demonstration</span>
        </div>
        <p className="text-sm text-steel mt-2 max-w-3xl">
          A generalized example of controlled-document writing style. All names, limits, identifiers and process details are fictional or
          intentionally redacted.
        </p>
        <div className="panel doc-paper mt-4 relative" data-testid="sop-excerpt">
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-cyan/15 bg-navy/60">
            <FileText className="w-3.5 h-3.5 text-cyan" aria-hidden="true" />
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-steel">
              SOP-PQ-### • Rev X • Synthetic — Not an employer-controlled document
            </span>
          </div>
          <div className="p-5 max-h-[480px] overflow-y-auto">{renderExcerpt(SOP_EXCERPT)}</div>
        </div>
        <div className="flex flex-wrap gap-1.5 mt-4" data-testid="sop-annotations">
          {ANNOTATIONS.map((a) => (
            <TechTag key={a}>{a}</TechTag>
          ))}
        </div>
        <div className="mt-4 max-w-2xl">
          <Disclaimer>
            This excerpt is entirely synthetic and redacted. It demonstrates writing style and document structure only — no employer-controlled
            content is reproduced.
          </Disclaimer>
        </div>
      </div>
    </div>
  );
}
